export interface ZLLanguage {
	// BCP 47 locale identifier (e.g. 'en-US', 'zh-CN'). (Always ab-cd format)
    // Used by i18next (translation loading and language switching) 
    // and Ant Design UI component localisation
    code: string;
    
    // User-facing language name shown in the language switcher
	displayText: string;

    // Whether the language reads right-to-left. 
    // Sets document.dir and Ant Design layout direction
	rtl: boolean;
    
    // Moment.js date/time formatting locale (e.g. 'en', 'fr', 'fr-ca', 'zh-cn').
    // (2 letter code unless all 4 letters are required to break ambiguity)
    // Also used as the URL path slug and hrefLang SEO attribute value.
	// Note: The app supports both US and AU English venues but only has a single 'en' moment locale,
	// so all user-facing dates must include the month name (e.g. 'D MMMM YYYY', 'LL', 'LLL')
	// to avoid ambiguity between MM/DD and DD/MM date order conventions.
	momentLocale: string;

    // Language code used to link to the matching page on the Zero Latency global website
    // Always 2 letter code
	globalWebsiteLangCode: string;
}

export const languageList : ZLLanguage[] = [
    { code: 'en-US', displayText: 'English', rtl: false, momentLocale: 'en', globalWebsiteLangCode: 'en' }, //en-US first, i18n defaults to en-US
    { code: 'ar-SA', displayText: 'العَرَبِيَّة‎', rtl: true, momentLocale: 'ar-sa', globalWebsiteLangCode: 'ar' },
    { code: 'ca-ES', displayText: 'Català', rtl: false, momentLocale: 'ca', globalWebsiteLangCode: 'ca' },
    { code: 'cs-CZ', displayText: 'čeština', rtl: false, momentLocale: 'cs', globalWebsiteLangCode: 'cs' },
    { code: 'da-DK', displayText: 'Dansk', rtl: false, momentLocale: 'da', globalWebsiteLangCode: 'da' },
    { code: 'de-DE', displayText: 'Deutsch', rtl: false, momentLocale: 'de', globalWebsiteLangCode: 'de' },
    { code: 'es-ES', displayText: 'Español', rtl: false, momentLocale: 'es', globalWebsiteLangCode: 'es' },
    { code: 'fr-FR', displayText: 'Français', rtl: false, momentLocale: 'fr', globalWebsiteLangCode: 'fr' }, // fr-FR first so generic browser 'fr' resolves to standard French
    { code: 'fr-CA', displayText: 'Français Canadien', rtl: false, momentLocale: 'fr-ca', globalWebsiteLangCode: 'fr' },
    { code: 'it-IT', displayText: 'Italiano', rtl: false, momentLocale: 'it', globalWebsiteLangCode: 'it' },
    { code: 'nl-NL', displayText: 'Dutch', rtl: false, momentLocale: 'nl', globalWebsiteLangCode: 'nl' },
    { code: 'pt-PT', displayText: 'Português', rtl: false, momentLocale: 'pt', globalWebsiteLangCode: 'pt' },
    { code: 'sk-SK', displayText: 'Slovenčina', rtl: false, momentLocale: 'sk', globalWebsiteLangCode: 'sk' },

    //global website does not support these languages so default to 'en'
    { code: 'ja-JP', displayText: '日本語', rtl: false, momentLocale: 'ja', globalWebsiteLangCode: 'en' },
    { code: 'th-TH', displayText: 'ภาษาไทย', rtl: false, momentLocale: 'th', globalWebsiteLangCode: 'en' },
    { code: 'zh-CN', displayText: '中文简体', rtl: false, momentLocale: 'zh-cn', globalWebsiteLangCode: 'en' },
    { code: 'zh-TW', displayText: '中文繁體', rtl: false, momentLocale: 'zh-tw', globalWebsiteLangCode: 'en' }
];

/** Checks if a URL path segment matches a known locale (by momentLocale or code, case-insensitive) */
export function isKnownLocale(segment: string): boolean {
	if (!segment) return false;
	const lower = segment.toLowerCase();
	return languageList.some((lang) => lang.momentLocale.toLowerCase() === lower || lang.code.toLowerCase() === lower);
}

/** Resolves a URL segment to its canonical momentLocale, or null if unrecognised */
export function resolveLocale(segment: string): string | null {
	if (!segment) return null;
	const lower = segment.toLowerCase();
	const lang = languageList.find(
		(lng) => lng.momentLocale.toLowerCase() === lower || lng.code.toLowerCase() === lower
	);
	return lang?.momentLocale ?? null;
}

/** Checks if a segment has the shape of a locale code (e.g. 'ko', 'pl-PL') without requiring it to be in our list */
export function looksLikeLocale(segment: string): boolean {
	return /^[a-z]{2}(-[a-z]{2,4})?$/i.test(segment);
}

/** Strips the language prefix from a URL pathname and optionally prepends a new one */
export function replaceLanguageInPath(pathname: string, newLanguage?: string): string {
	const segments = pathname.split('/').filter((s) => s !== '');
	const basePath = segments.slice(1).join('/');
	if (newLanguage) {
		return `/${newLanguage}${basePath ? `/${basePath}` : ''}`;
	}
	return basePath ? `/${basePath}` : '/';
}
